import { FormattedDateTime } from '@10xLabs/react-web/components/FormattedDateTime'
import { FormattedNumber } from '@10xLabs/react-web/components/FormattedNumber'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import Heading from 'components/v2/Heading'
import { noteCategoryMessages, noteSubcategoryMessages } from 'features/Trips/components/NoteCategory/messages'
import { TripV2Note, TripV2NoteCategory, TripV2NoteChangeType, TripNoteDriverRsvpStatus } from 'gql/generated/graphql'
import { safeFormatMessage } from 'utils/intl'
import { centsToInteger, metersToKilometers } from 'utils/number'
import { GTCashFlowTypeInput } from 'gql/types.generated'
import { FilePreview } from 'components/v2/InputFile'

const NoteWrapper = styled.div`
  background-color: #eaeaff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 16px;
`

const NoteHeader = styled.div`
  text-transform: capitalize;
`

const NoteDetail = styled(Heading.Caption)`
  font-size: 14px;
  text-transform: capitalize;
`
const NoteMessage = styled(Heading.Caption)`
  font-size: 14px;
`

const NoteCaption = styled(Heading.Caption)`
  color: #b0b0b0;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
`

const DriverRsvpStatus = styled.span`
  text-transform: lowercase;
`

export const operationTypeOptions = defineMessages({
  [GTCashFlowTypeInput.Income]: {
    id: 'trip.drawer.notes.cashFlow.type.income',
    defaultMessage: 'Income'
  },
  [GTCashFlowTypeInput.Expense]: {
    id: 'trip.drawer.notes.cashFlow.type.expense',
    defaultMessage: 'Expense'
  }
})

const changeTypeMessages = defineMessages({
  [TripV2NoteChangeType.AddNote]: {
    id: 'trip.drawer.notes.changeType.addNote',
    defaultMessage: 'Add Note'
  },
  [TripV2NoteChangeType.TripOdometerDistanceRegistered]: {
    id: 'trip.drawer.notes.changeType.tripOdometerDistanceRegistered',
    defaultMessage: 'Add Note'
  },
  [TripV2NoteChangeType.AssignDriver]: {
    id: 'trip.drawer.notes.changeType.assignDriver',
    defaultMessage: 'Assign Driver'
  },
  [TripV2NoteChangeType.CancelTrip]: {
    id: 'trip.drawer.notes.changeType.cancelTrip',
    defaultMessage: 'Cancel Trip'
  },
  [TripV2NoteChangeType.ChangeVehicle]: {
    id: 'trip.drawer.notes.changeType.changeVehicle',
    defaultMessage: 'Change Vehicle'
  },
  [TripV2NoteChangeType.AddDriverRsvpNote]: {
    id: 'trip.drawer.notes.changeType.addDriverRsvpNote',
    defaultMessage: 'Driver'
  }
})

const driverRsvpStatusMessages = defineMessages({
  [TripNoteDriverRsvpStatus.Confirmed]: {
    id: 'trip.drawer.notes.driverRsvpStatus.confirmed',
    defaultMessage: 'Confirmed'
  },
  [TripNoteDriverRsvpStatus.Declined]: {
    id: 'trip.drawer.notes.driverRsvpStatus.declined',
    defaultMessage: 'Declined'
  }
})

interface INoteItemProps {
  note: TripV2Note
}

export const NoteItem = ({ note }: INoteItemProps): ReactElement => {
  const intl = useIntl()

  const isDriverRsvpNote = note.category === TripV2NoteCategory.DriverRsvp
  const shouldShowCategory = !!note.category && !isDriverRsvpNote

  return (
    <>
      <NoteWrapper>
        {!!note.changeType && (
          <NoteHeader>
            <Heading.Body bold>
              {safeFormatMessage({ intl, messages: changeTypeMessages, key: note.changeType })}
              {isDriverRsvpNote && (
                <>
                  {` ${note.driver?.firstName} ${note.driver?.lastName}`}
                  <DriverRsvpStatus>
                    {` ${safeFormatMessage({
                      intl,
                      messages: driverRsvpStatusMessages,
                      key: note.driver?.rsvpStatus as string
                    })}`}
                  </DriverRsvpStatus>
                </>
              )}
            </Heading.Body>
          </NoteHeader>
        )}
        <div>
          <div>
            {shouldShowCategory && (
              <NoteDetail>
                {safeFormatMessage({
                  intl,
                  messages: noteCategoryMessages,
                  key: note.category as TripV2NoteCategory
                }).toLowerCase()}
              </NoteDetail>
            )}
            {!!note.subcategory && (
              <NoteDetail>{` - ${safeFormatMessage({
                intl,
                messages: noteSubcategoryMessages,
                key: note.subcategory
              })}`}</NoteDetail>
            )}
          </div>
          {note.cashFlow?.type && (
            <div>
              <NoteDetail>
                <FormattedMessage
                  id="trips.drawer.notes.note.operationType"
                  defaultMessage="<strong>Operation type:</strong> {operationType}"
                  values={{
                    strong: (msg) => <strong>{msg}</strong>,
                    operationType: safeFormatMessage({ intl, messages: operationTypeOptions, key: note.cashFlow?.type })
                  }}
                />
              </NoteDetail>
            </div>
          )}
          {note.cashFlow?.amount && (
            <div>
              <NoteDetail>
                <FormattedMessage
                  id="trips.drawer.notes.empty.description"
                  defaultMessage="<strong>Amount:</strong> {amount}"
                  values={{
                    strong: (msg) => <strong>{msg}</strong>,
                    amount: <FormattedNumber value={centsToInteger(note.cashFlow.amount)} prefix="$" />
                  }}
                />
              </NoteDetail>
            </div>
          )}
          {note.distance && (
            <div>
              <NoteDetail>
                <FormattedMessage
                  id="trips.drawer.notes.note.distance"
                  defaultMessage="<strong>Real Distance:</strong> {amount} Km"
                  values={{
                    strong: (msg) => <strong>{msg}</strong>,
                    amount: metersToKilometers(note.distance)
                  }}
                />
              </NoteDetail>
            </div>
          )}
          {note.message && (
            <div>
              <NoteMessage>{note.message}</NoteMessage>
            </div>
          )}
        </div>
        <NoteCaption>
          <div>
            <FormattedDateTime date={note.createdAt} intl={intl} />
          </div>
          {note.user?.id && (
            <div>
              <FormattedMessage
                id="trips.drawer.notes.note.author"
                defaultMessage="By {author}"
                values={{ author: `${note.user?.firstName} ${note.user?.lastName}` }}
              />
            </div>
          )}
        </NoteCaption>
      </NoteWrapper>
      {note.files?.map((file, index) => (
        <FilePreview key={index} file={file.path} filename={file.name} />
      ))}
    </>
  )
}
